import React, { useEffect, useState, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import '../css/Messages.css';
import CreateMessage from '../components/CreateMessage';
import MessageView from '../components/MessageView';
import { FiTrash2, FiMail, FiInbox, FiSend, FiFileText, FiStar, FiSettings, FiSearch, FiCheckSquare, FiSquare, FiPaperclip, FiStar as FiStarIcon } from 'react-icons/fi';
import { useMessaging } from '../hooks/useMessaging';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { toast } from 'react-toastify';

const FOLDERS = [
  { key: 'inbox', label: 'Bejövő', icon: <FiInbox />, color: 'var(--text-color)' },
  { key: 'sent', label: 'Elküldött', icon: <FiSend />, color: 'var(--text-color)' },
  { key: 'drafts', label: 'Piszkozatok', icon: <FiFileText />, color: 'var(--text-color)' },
  { key: 'favorites', label: 'Kedvencek', icon: <FiStar />, color: 'var(--text-color)' },
  { key: 'system', label: 'Rendszer', icon: <FiSettings />, color: 'var(--text-color)' },
  { key: 'trash', label: 'Kuka', icon: <FiTrash2 />, color: 'var(--text-color)' },
];

const Messages = ({ isSidebarClosed, setIsSidebarClosed }) => {
  const {
    messages,
    loading,
    error,
    currentFolder,
    hasMore,
    folderStats,
    users,
    loadMessages,
    saveDraft,
    updateDraft,
    sendMessage,
    markAsRead,
    deleteMessage,
    bulkActions,
    loadMore,
    uploadAttachment,
    getMessageAttachments,
    loadFolderStats,
    toggleFavorite,

    setCurrentFolder,
    setError
  } = useMessaging();

  const truncateSubject = (subject, maxLength = 50) => {
    if (!subject || subject.length <= maxLength) {
      return subject;
    }
    return subject.substring(0, maxLength) + '...';
  };

  const getPriorityLabel = (priority) => {
    const priorityMap = {
      'low': 'Alacsony',
      'normal': 'Normál',
      'high': 'Magas',
      'urgent': 'Sürgős'
    };
    return priorityMap[priority] || priority;
  };



  const [search, setSearch] = useState('');
  const [showCompose, setShowCompose] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);


  const refreshFunction = useCallback(async () => {
    if (currentFolder && !loading) {
      await loadMessages(currentFolder, 1, search);
      await loadFolderStats();
    }
  }, [currentFolder, loading, search, loadMessages, loadFolderStats]);

  const { lastRefresh } = useAutoRefresh(refreshFunction, 30000);

  const filteredMessages = messages.filter(m =>
    (!search || 
     m.subject?.toLowerCase().includes(search.toLowerCase()) || 
     m.from_user_name?.toLowerCase().includes(search.toLowerCase()) ||
     m.recipients?.some(r => r.user_name?.toLowerCase().includes(search.toLowerCase())))
  );

  const handleFolderChange = (folder) => {
    setCurrentFolder(folder);
    setSelectedMessage(null);
    setShowCompose(false);
    setEditingDraft(null); 
    setSelectedMessages(new Set());
    setShowBulkActions(false);
    
    
    if (folder === 'trash' && currentFolder === 'trash') {
      
      return;
    } else {
      
      loadMessages(folder, 1, search);
    }
  };

  const handleSearch = (searchTerm) => {
    setSearch(searchTerm);
    setEditingDraft(null); 
    loadMessages(currentFolder, 1, searchTerm);
  };

  const handleMessageClick = async (message) => {
    
    if (currentFolder === 'drafts') {
      handleEditDraft(message);
      return;
    }

    if (!message.read) {
      await bulkActions('markAsRead', [message.id]);
      
      // Frissítjük a mappa statisztikákat is
      await loadFolderStats();
    }
    setSelectedMessage(message);
  };

  const handleSendMessage = async (formData) => {
    try {
      const result = await sendMessage(formData);
      toast.success('Üzenet sikeresen elküldve!');
      setShowCompose(false);
      
      // Frissítjük a mappa statisztikákat is
      await loadFolderStats();
      
      return result;
    } catch (error) {
      toast.error('Hiba az üzenet küldésekor: ' + error.message);
      throw error;
    }
  };

  const handleSaveDraft = async (formData) => {
    try {
      let result;
      if (editingDraft) {

        result = await updateDraft(editingDraft.id, formData);
        toast.success('Piszkozat sikeresen frissítve!');
      } else {

        result = await saveDraft(formData);
        toast.success('Piszkozat sikeresen mentve!');
      }
      setShowCompose(false);
      setEditingDraft(null);
      
      // Frissítjük a mappa statisztikákat
      await loadFolderStats();
      
      return result;
    } catch (error) {
      toast.error('Hiba a piszkozat mentésekor: ' + error.message);
      throw error;
    }
  };

  const handleEditDraft = (draft) => {
    setEditingDraft(draft);
    setShowCompose(true);
    setSelectedMessage(null);
  };

  const handleSendDraft = async (formData) => {
    try {

      if (editingDraft) {
        await deleteMessage(editingDraft.id);
      }
      

      const result = await sendMessage(formData);
      toast.success('Üzenet sikeresen elküldve!');
      setShowCompose(false);
      setEditingDraft(null);

      await loadFolderStats();
      
      return result;
    } catch (error) {
      toast.error('Hiba az üzenet küldésekor: ' + error.message);
      throw error;
    }
  };

  const handleDeleteMessage = async (id) => {
    try {
      if (currentFolder === 'trash') {
        // Ha a Kuka mappából törölünk, akkor végleges törlés
        await bulkActions('delete', [id]);
        toast.success('Üzenet véglegesen törölve!');
      } else {
        // Egyéb mappákból a Kuka mappába helyezzük át
        await deleteMessage(id);
        toast.success('Üzenet a Kuka mappába helyezve!');
      }
      
      if (selectedMessage?.id === id) {
        setSelectedMessage(null);
      }
    } catch (error) {
      toast.error('Hiba az üzenet törlésekor: ' + error.message);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedMessages.size === 0) {
      toast.warning('Válassz ki legalább egy üzenetet!');
      return;
    }

    try {
      await bulkActions(action, Array.from(selectedMessages));
      toast.success(`${selectedMessages.size} üzenet sikeresen ${action === 'delete' ? 'törölve' : 'módosítva'}!`);
      setSelectedMessages(new Set());
      setShowBulkActions(false);
      
      // Frissítjük a mappa statisztikákat is
      await loadFolderStats();
    } catch (error) {
      toast.error('Hiba a tömeges művelet végrehajtásakor: ' + error.message);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMessages.size === 0) {
      toast.warning('Válassz ki legalább egy üzenetet!');
      return;
    }

    try {
      if (currentFolder === 'trash') {
        // Ha a Kuka mappából törölünk, akkor végleges törlés
        await bulkActions('delete', Array.from(selectedMessages));
        toast.success(`${selectedMessages.size} üzenet véglegesen törölve!`);
      } else {
        // Egyéb mappákból a Kuka mappába helyezzük át őket
        await bulkActions('move_to_trash', Array.from(selectedMessages));
        toast.success(`${selectedMessages.size} üzenet a Kuka mappába helyezve!`);
      }
      
      setSelectedMessages(new Set());
    } catch (error) {
      toast.error('Hiba a tömeges törléskor: ' + error.message);
    }
  };

  const handleBulkMarkAsRead = async () => {
    if (selectedMessages.size === 0) {
      toast.warning('Válassz ki legalább egy üzenetet!');
      return;
    }

    try {
      await bulkActions('markAsRead', Array.from(selectedMessages));
      toast.success(`${selectedMessages.size} üzenet sikeresen olvasottként jelölve!`);
      setSelectedMessages(new Set());
      
      // Frissítjük a mappa statisztikákat is
      await loadFolderStats();
    } catch (error) {
      toast.error('Hiba a tömeges olvasottként jelöléskor: ' + error.message);
    }
  };

  const toggleMessageSelection = (messageId) => {
    const newSelected = new Set(selectedMessages);
    if (newSelected.has(messageId)) {
      newSelected.delete(messageId);
    } else {
      newSelected.add(messageId);
    }
    setSelectedMessages(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const selectAllMessages = () => {
    if (selectedMessages.size === filteredMessages.length) {
      setSelectedMessages(new Set());
      setShowBulkActions(false);
    } else {
      setSelectedMessages(new Set(filteredMessages.map(m => m.id)));
      setShowBulkActions(true);
    }
  };

  const handleToggleFavorite = async (messageId, e) => {
    if (e) e.stopPropagation();
    try {
      await toggleFavorite(messageId);
      toast.success('Kedvencek frissítve!');
    } catch (error) {
      toast.error('Hiba a kedvencek frissítésekor: ' + error.message);
    }
  };

  const getFolderCount = (folderKey) => {
    if (!folderStats[folderKey]) return 0;
    return folderStats[folderKey].total || 0;
  };

  const getUnreadCount = (folderKey) => {
    if (!folderStats[folderKey]) return 0;
    return folderStats[folderKey].unread || 0;
  };

  if (error) {
    return (
      <div>
        <Navbar isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} />
        <Sidebar isCollapsed={isSidebarClosed} setIsCollapsed={setIsSidebarClosed} />
        <div className="page-content messages-page">
          <div className="error-container">
            <div className="error-message">
              Hiba: {error}
              <button onClick={() => setError(null)}>Újra próbálkozás</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} />
      <Sidebar isCollapsed={isSidebarClosed} setIsCollapsed={setIsSidebarClosed} />
      <div className="page-content messages-page">
        <div className="messages-layout">

          <div className="messages-sidebar">
            <button className="messages-new-btn" onClick={() => setShowCompose(true)}>
              <FiMail style={{marginRight: 8}} />
              Új üzenet
            </button>
            
            <div className="messages-folders">
              <div className="messages-folders-title">Mappák</div>
              {FOLDERS.map(folder => (
                <div
                  key={folder.key}
                  className={`messages-folder${currentFolder === folder.key ? ' active' : ''}`}
                  onClick={() => handleFolderChange(folder.key)}
                  style={{ color: 'var(--text-color)' }}
                >
                  <span className="messages-folder-icon">{folder.icon}</span>
                  {folder.label}
                  <span className={`messages-folder-count${getFolderCount(folder.key) ? '' : ' empty'}`}>
                    {getFolderCount(folder.key)}
                  </span>
                  {getUnreadCount(folder.key) > 0 && (
                    <span className="messages-folder-unread">{getUnreadCount(folder.key)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="messages-main">
            <div className="messages-header-row">
              <div className="messages-breadcrumbs">
                <a href="/">Főoldal</a> / Levelezés - {FOLDERS.find(f => f.key === currentFolder)?.label}
              </div>
              {!selectedMessage && (
                <div className="messages-search-wrap">
                  <input
                    className="messages-search-input"
                    placeholder="Keresés"
                    value={search}
                    onChange={e => handleSearch(e.target.value)}
                  />
                  <button className="messages-search-btn">
                    <FiSearch />
                  </button>
                </div>
              )}
            </div>

            
            
            {showCompose ? (
              <CreateMessage 
                onClose={() => {
                  setShowCompose(false);
                  setEditingDraft(null);
                }}
                users={users}
                onSend={editingDraft ? handleSendDraft : handleSendMessage}
                onSaveDraft={handleSaveDraft}
                onUpdateDraft={updateDraft}
                draft={editingDraft}
                uploadAttachment={uploadAttachment}
              />
            ) : selectedMessage ? (
              <MessageView 
                message={selectedMessage} 
                currentFolder={currentFolder}
                onBack={() => setSelectedMessage(null)}
                onDelete={handleDeleteMessage}
                onReply={() => {
                  // Open CreateMessage in reply mode
                  setShowCompose(true);
                  setEditingDraft({
                    ...selectedMessage,
                    isReply: true,
                    replyToMessage: selectedMessage
                  });
                }}
                users={users}
                onRefresh={() => loadMessages(currentFolder, 1, search)}
                getMessageAttachments={getMessageAttachments}
                onToggleFavorite={handleToggleFavorite}
              />
            ) : (
              <div className="messages-list">
                {loading && messages.length === 0 ? (
                  <div className="messages-loading">Betöltés...</div>
                ) : filteredMessages.length === 0 ? (
                  <div className="messages-empty">
                    {search ? 'Nincs találat a keresésre.' : 'Nincs üzenet ebben a mappában.'}
                  </div>
                ) : (
                  <>
                                                                                     <div className="messages-list-header">
                         <div className="messages-list-select-all">
                           <button 
                             onClick={selectAllMessages}
                             className="select-all-btn"
                           >
                             {selectedMessages.size === filteredMessages.length ? 
                               <FiCheckSquare /> : <FiSquare />
                             }
                           </button>
                         </div>
                         <div className="messages-list-actions">
                           <button 
                             className="messages-list-action" 
                             onClick={handleBulkDelete}
                             disabled={selectedMessages.size === 0}
                             title="Kiválasztott üzenetek törlése"
                           >
                             <FiTrash2 />
                           </button>
                           <button 
                             className="messages-list-action" 
                             onClick={handleBulkMarkAsRead}
                             disabled={selectedMessages.size === 0}
                             title="Kiválasztott üzenetek olvasottként jelölése"
                           >
                             <FiMail />
                           </button>
              </div>
            </div>
            
                    {filteredMessages.map(msg => (
                      <div 
                        className={`messages-list-row${msg.read ? '' : ' unread'}`} 
                        key={msg.id} 
                        onClick={() => handleMessageClick(msg)}
                      >
                        <div className="messages-list-checkbox" onClick={e => e.stopPropagation()}>
                          <input 
                            type="checkbox" 
                            checked={selectedMessages.has(msg.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleMessageSelection(msg.id);
                            }}
                          />
                          <button 
                            className="messages-list-action" 
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteMessage(msg.id);
                            }}
                            title="Törlés"
                          >
                            <FiTrash2 />
                          </button>
                                            <button 
                            className="messages-list-action" 
                            onClick={e => {
                              e.stopPropagation();
                              if (!msg.read) {
                                bulkActions('markAsRead', [msg.id]);
                              }
                            }}
                            title={msg.read ? 'Olvasott' : 'Olvasottként jelöl'}
                          >
                            <FiMail />
                          </button>
                          {/* Ne jelenjen meg a csillag ikon, ha az üzenet már a kedvencek mappában van */}
                          {currentFolder !== 'favorites' && (
                            <button 
                              className={`messages-list-action ${msg.is_favorite ? 'favorite-active' : ''}`}
                              onClick={(e) => handleToggleFavorite(msg.id, e)}
                              title={msg.is_favorite ? 'Kedvencekből eltávolítás' : 'Kedvencekhez adás'}
                            >
                              <FiStarIcon style={{ color: msg.is_favorite ? '#ffd700' : 'var(--color-text-gray)' }} />
                            </button>
                          )}
                        </div>
                        <div className="messages-list-from">
                          {msg.from || 'Ismeretlen'}
                        </div>
                        <div className="messages-list-subject" title={msg.subject || 'Nincs tárgy'}>
                          {truncateSubject(msg.subject) || 'Nincs tárgy'}
                          {msg.has_attachment && <FiPaperclip size={14} style={{marginLeft: 8, color: '#666'}} title="Van csatolmány" />}
                        </div>
                                                                          <div className="messages-list-date">
                            {msg.date ? new Date(msg.date).toLocaleDateString('hu-HU') : 
                             msg.created_at ? new Date(msg.created_at).toLocaleDateString('hu-HU') : ''}
                          </div>
                          <div className="messages-list-priority">
                            <span className={`priority-${msg.priority || 'normal'}`}>
                              {getPriorityLabel(msg.priority)}
                            </span>
                          </div>
              </div>
                    ))}
                    
                    {hasMore && (
                      <div className="messages-load-more">
                        <button 
                          className="load-more-button"
                          onClick={loadMore}
                          disabled={loading}
                        >
                          {loading ? 'Betöltés...' : 'További üzenetek betöltése'}
                        </button>
                    </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages; 